import React from 'react';
import { Box, Typography, Card, CardContent, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../CurrencyTextField';

export function parseJsonbStringArray(val) {
  if (val == null) return [];
  if (Array.isArray(val)) return val.map((s) => String(s || '').trim()).filter(Boolean);
  if (typeof val === 'string') {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p.map((s) => String(s || '').trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function normalizeSowListStyle(v) {
  const s = String(v == null || v === '' ? 'bullets' : v).trim().toLowerCase();
  if (s === 'numbered' || s === 'number') return 'numbered';
  if (s === 'none' || s === 'plain') return 'none';
  return 'bullets';
}

/** Print layout: ul / ol / plain blocks (matches Quote print). */
export function renderPrintSowItemList(items, listStyle, keyPrefix) {
  const style = normalizeSowListStyle(listStyle);
  if (!items.length) return null;
  const itemClass = 'print-sow-paragraph';
  const preWrap = { whiteSpace: 'pre-wrap' };
  if (style === 'numbered') {
    return (
      <ol className="print-sow-list">
        {items.map((text, i) => (
          <li key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
            {text}
          </li>
        ))}
      </ol>
    );
  }
  if (style === 'none') {
    return (
      <div className="print-sow-plain">
        {items.map((text, i) => (
          <div key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
            {text}
          </div>
        ))}
      </div>
    );
  }
  return (
    <ul className="print-sow-list">
      {items.map((text, i) => (
        <li key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
          {text}
        </li>
      ))}
    </ul>
  );
}

function renderScreenList(items, listStyle) {
  const style = normalizeSowListStyle(listStyle);
  if (!items.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (style === 'numbered') {
    return (
      <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1 } }}>
        {items.map((text, i) => (
          <Typography key={i} component="li" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {text}
          </Typography>
        ))}
      </Box>
    );
  }
  if (style === 'none') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((text, i) => (
          <Typography key={i} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {text}
          </Typography>
        ))}
      </Box>
    );
  }
  return (
    <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: 'disc', '& li': { mb: 1 } }}>
      {items.map((text, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {text}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * On Order Detail: Field Work / Service panel — mirrors Quote layout when linked quote has SOW data.
 */
export function FieldWorkOrderDetailCard({ order }) {
  const scope = parseJsonbStringArray(order?.source_quote_sow_scope_sections);
  const clar = parseJsonbStringArray(order?.source_quote_sow_clarifications_sections);
  const listStyle = normalizeSowListStyle(order?.source_quote_sow_list_style);
  const showBreakdown = !!order?.source_quote_proposal_show_breakdown;
  const proposalTotal =
    order?.source_quote_proposal_total_amount != null && order?.source_quote_proposal_total_amount !== ''
      ? Number(order.source_quote_proposal_total_amount)
      : null;
  const materials = Number(order?.source_quote_proposal_materials_amount) || 0;
  const labor = Number(order?.source_quote_proposal_labor_amount) || 0;
  const rentals = Number(order?.source_quote_proposal_rentals_amount) || 0;
  const hasLinkedQuoteSow =
    order?.quote_id != null &&
    (scope.length > 0 || clar.length > 0 || proposalTotal != null || showBreakdown);

  return (
    <Card sx={{ mb: 1.5 }}>
      <CardContent sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Field Work / Service
          </Typography>
          {order?.quote_id != null ? (
            <Typography variant="caption" color="text.secondary">
              Scope and proposal amounts are taken from the{' '}
              <MuiLink component={Link} to={`/quotes/${order.quote_id}`} underline="hover">
                linked quote {order.quote_identifier || order.quote_number || `#${order.quote_id}`}
              </MuiLink>
              . Edit the quote to change them; use Shipping and Fulfillment or packing slips for operational tracking.
            </Typography>
          ) : null}
        </Box>

        {hasLinkedQuoteSow ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
              Scope of work
            </Typography>
            {scope.length > 0 ? (
              renderScreenList(scope, listStyle)
            ) : (
              <Typography variant="body2" color="text.secondary">
                No scope sections on the linked quote.
              </Typography>
            )}

            {clar.length > 0 ? (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Clarifications and exceptions
                </Typography>
                {renderScreenList(clar, listStyle)}
              </>
            ) : null}

            <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Proposal
              </Typography>
              {showBreakdown ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                    <Typography variant="body2">Materials</Typography>
                    <Typography variant="body2">{formatCurrency(materials)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                    <Typography variant="body2">Labor</Typography>
                    <Typography variant="body2">{formatCurrency(labor)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                    <Typography variant="body2">Rentals</Typography>
                    <Typography variant="body2">{formatCurrency(rentals)}</Typography>
                  </Box>
                </Box>
              ) : null}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Total proposal amount:{' '}
                {proposalTotal != null && Number.isFinite(proposalTotal) ? formatCurrency(proposalTotal) : '—'}
              </Typography>
            </Box>

            {String(order.notes ?? '').trim() ? (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Comments
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {String(order.notes).trim()}
                </Typography>
              </>
            ) : null}
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              No linked quote scope found. Summary below is from this order&apos;s description and notes.
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: order.notes ? 2 : 0 }}>
              {order.description?.trim() || '—'}
            </Typography>
            {order.notes ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {order.notes}
                </Typography>
              </>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Print-only fragment: Field Work blocks (quote-style), or fallback description.
 */
export function FieldWorkOrderPrintSection({ order, hideDescriptionBlock }) {
  const scope = parseJsonbStringArray(order?.source_quote_sow_scope_sections);
  const clar = parseJsonbStringArray(order?.source_quote_sow_clarifications_sections);
  const listStyle = normalizeSowListStyle(order?.source_quote_sow_list_style);
  const showBreakdown = !!order?.source_quote_proposal_show_breakdown;
  const proposalTotal =
    order?.source_quote_proposal_total_amount != null && order?.source_quote_proposal_total_amount !== ''
      ? Number(order.source_quote_proposal_total_amount)
      : null;
  const materials = Number(order?.source_quote_proposal_materials_amount) || 0;
  const labor = Number(order?.source_quote_proposal_labor_amount) || 0;
  const rentals = Number(order?.source_quote_proposal_rentals_amount) || 0;
  const hasLinkedQuoteSow =
    order?.quote_id != null &&
    (scope.length > 0 || clar.length > 0 || proposalTotal != null || showBreakdown);

  if (!hasLinkedQuoteSow) {
    if (hideDescriptionBlock) return null;
    if (!order.description?.trim()) return null;
    return (
      <div className="print-section" style={{ marginBottom: '20px', marginTop: '10px' }}>
        <strong>Description:</strong> {order.description}
      </div>
    );
  }

  return (
    <>
      <div className="print-sow-block" style={{ marginBottom: 12 }}>
        <div className="print-sow-heading">Field Work / Service</div>
        <div style={{ fontSize: '12px', marginBottom: 8, color: '#444' }}>
          Linked quote: {order.quote_identifier || order.quote_number || `#${order.quote_id}`}
        </div>
        <div className="print-sow-heading" style={{ fontSize: '13px', marginTop: 8 }}>
          Scope of work
        </div>
        {scope.length > 0 ? (
          renderPrintSowItemList(scope, listStyle, 'print-order-sow-scope')
        ) : (
          <div style={{ color: '#666' }}>No scope provided.</div>
        )}
      </div>
      {clar.length > 0 ? (
        <div className="print-sow-block" style={{ marginBottom: 12 }}>
          <div className="print-sow-heading">Clarifications and exceptions</div>
          {renderPrintSowItemList(clar, listStyle, 'print-order-sow-clar')}
        </div>
      ) : null}
      {showBreakdown ? (
        <table className="print-table print-quote-line-table print-table-no-zebra" style={{ border: 'none', marginBottom: 8 }}>
          <tbody>
            <tr>
              <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Materials</td>
              <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(materials)}</td>
            </tr>
            <tr>
              <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Labor</td>
              <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(labor)}</td>
            </tr>
            <tr>
              <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Rentals</td>
              <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(rentals)}</td>
            </tr>
          </tbody>
        </table>
      ) : null}
      <div className="print-quote-total-row" style={{ marginBottom: 16 }}>
        <strong>Total proposal amount:</strong>{' '}
        <span>{proposalTotal != null && Number.isFinite(proposalTotal) ? formatCurrency(proposalTotal) : '—'}</span>
      </div>
    </>
  );
}
